import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type { CustomPackCheck, ShopCatalog, ShopProduct } from '@shared/types';
import { Footer, TopBar, useDocumentTitle } from '../../components/Chrome';
import { DeckMock } from './DeckMock';
import { formatPrice, shopApi } from './api';

const MAX_QUANTITY = 10;

function PackChecker({
  initial,
  check,
  onCheck,
}: {
  initial: string;
  check: CustomPackCheck | null;
  onCheck: (c: CustomPackCheck | null) => void;
}) {
  const [code, setCode] = useState(initial);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const last = useRef('');

  async function run(value: string) {
    const clean = value.trim().toUpperCase();
    if (!clean || clean === last.current) return;
    last.current = clean;
    setBusy(true);
    setError('');
    try {
      onCheck(await shopApi.checkPack(clean));
    } catch (e) {
      onCheck(null);
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // A code passed in the link (?pack=CODE) is checked straight away.
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current || !initial) return;
    ran.current = true;
    void run(initial);
  });

  const short = check && !check.ok ? check.minCards - check.cardCount : 0;
  return (
    <section className="panel stack">
      <div>
        <h2 style={{ marginBottom: 4 }}>Your custom pack</h2>
        <p className="muted small" style={{ margin: 0 }}>
          Type the code from your pack page. It needs at least 20 cards. Photo cards print too.
        </p>
      </div>
      <div className="row">
        <input
          className="input code grow"
          style={{ minWidth: 150 }}
          value={code}
          maxLength={12}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          placeholder="CODE"
          aria-label="Custom pack code"
          onChange={(e) => {
            const v = e.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase();
            setCode(v);
            if (check && v !== check.code) onCheck(null);
            if (v.length === 6) void run(v);
          }}
          onBlur={() => void run(code)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              last.current = '';
              void run(code);
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline"
          disabled={!code || busy}
          onClick={() => {
            last.current = '';
            void run(code);
          }}
        >
          {busy ? 'Checking...' : 'Check'}
        </button>
      </div>
      {error && <p className="error" style={{ margin: 0 }}>{error}</p>}
      {check && (
        <div className={`pack-check ${check.ok ? 'ok' : 'short'}`} role="status">
          <strong>
            {check.ok ? '✓ ' : ''}
            {check.name}
          </strong>
          <span>
            {check.promptCount} prompts · {check.answerCount} answers
            {check.photoCount > 0 && ` · ${check.photoCount} photo ${check.photoCount === 1 ? 'card' : 'cards'}`}
          </span>
          {check.ok ? (
            <span className="small">All {check.cardCount} cards get printed and shuffled into the Classic deck.</span>
          ) : (
            <span className="small">
              That's {check.cardCount} cards. Add {short} more to print it.{' '}
              <Link to={`/packs/${check.code}`} target="_blank">
                Open your pack
              </Link>
            </span>
          )}
        </div>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        No pack yet? <Link to="/packs/new">Make one for free</Link>. Fill it with cousins' names, family sayings and photos.
      </p>
    </section>
  );
}

export function ProductPage() {
  const slug = useParams().slug ?? '';
  const [params] = useSearchParams();
  const [data, setData] = useState<{ checkout: ShopCatalog['checkout']; product: ShopProduct } | null>(null);
  const [loadError, setLoadError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [check, setCheck] = useState<CustomPackCheck | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preordered, setPreordered] = useState<string | null>(null);
  useDocumentTitle(data?.product.name ?? 'Card decks');

  useEffect(() => {
    shopApi.product(slug).then(setData).catch((e) => setLoadError(e.message));
  }, [slug]);

  if (loadError) {
    return (
      <>
        <TopBar />
        <main className="page stack">
          <h1>Deck not found</h1>
          <p className="muted">{loadError}</p>
          <Link to="/shop">See all decks</Link>
        </main>
      </>
    );
  }
  if (!data) return <TopBar />;

  const { product, checkout } = data;
  const custom = product.kind === 'custom_deck';
  const preorder = checkout === 'preorder';
  const total = formatPrice(product.priceCents * quantity, product.currency);
  const blocked = custom && !check?.ok;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (blocked) {
      setError(check ? 'Your pack needs a few more cards first.' : 'Check your custom pack code first.');
      return;
    }
    setBusy(true);
    try {
      const result = await shopApi.checkout({
        productSlug: product.slug,
        quantity,
        customPackCode: custom ? check?.code : undefined,
        name: name || undefined,
        email: email || undefined,
      });
      if ('url' in result) {
        window.location.assign(result.url);
        return;
      }
      setPreordered(result.publicId);
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  }

  return (
    <>
      <TopBar />
      <main className="page wide shop">
        <p className="small" style={{ marginTop: 0 }}>
          <Link to="/shop">← All decks</Link>
        </p>
        <div className="product-layout">
          <div className="product-visual">
            <DeckMock product={product} size="lg" />
          </div>
          <div className="stack">
            <div>
              <h1 style={{ marginBottom: 4 }}>{product.name}</h1>
              <p className="price big">{formatPrice(product.priceCents, product.currency)}</p>
              <p>{product.description}</p>
            </div>
            <div className="panel stack">
              <h2 style={{ margin: 0 }}>What's inside</h2>
              <ul className="inside-list">
                {custom && <li>Every card in your custom pack, photos included</li>}
                {product.packs.map((p) => (
                  <li key={p.slug}>
                    <strong>{p.name}</strong>: {p.promptCount > 0 && `${p.promptCount} prompts`}
                    {p.promptCount > 0 && p.answerCount > 0 && ', '}
                    {p.answerCount > 0 && `${p.answerCount} answers`}
                  </li>
                ))}
                <li>Poker-size cards (2.5 x 3.5 in) on matte cardstock, in a tuck box</li>
                <li>Translations printed under the Hindi and Urdu lines</li>
              </ul>
            </div>

            {preordered ? (
              <section className="panel stack center preorder-done" role="status">
                <span className="big-emoji" aria-hidden="true">
                  🎉
                </span>
                <h2 style={{ margin: 0 }}>You're on the list!</h2>
                <p style={{ margin: 0 }}>We'll email you when decks ship. Nothing to pay today.</p>
                <Link className="btn btn-outline" to={`/shop/thanks?order=${preordered}`}>
                  See your pre-order
                </Link>
                <Link to="/">Play free while you wait</Link>
              </section>
            ) : (
              <form className="stack" onSubmit={submit}>
                {custom && <PackChecker initial={(params.get('pack') ?? '').toUpperCase()} check={check} onCheck={setCheck} />}
                <div className="row between qty-row">
                  <span className="field-label" id="qty-label">
                    How many decks?
                  </span>
                  <div className="stepper" role="group" aria-labelledby="qty-label">
                    <button type="button" className="icon-btn" aria-label="One fewer" disabled={quantity <= 1} onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                      −
                    </button>
                    <output aria-live="polite">{quantity}</output>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="One more"
                      disabled={quantity >= MAX_QUANTITY}
                      onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>
                {preorder && (
                  <div className="grid-2">
                    <label className="field">
                      Your name
                      <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={80} required />
                    </label>
                    <label className="field">
                      Email
                      <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" maxLength={200} required />
                    </label>
                  </div>
                )}
                {error && <p className="error" style={{ margin: 0 }}>{error}</p>}
                <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
                  {busy ? 'One sec...' : preorder ? `Pre-order · ${total}` : `Checkout · ${total}`}
                </button>
                <p className="small muted center" style={{ margin: 0 }}>
                  {preorder
                    ? "No payment today. We'll email you when decks ship."
                    : "Secure payment by Stripe. You'll add your shipping address there."}
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
